import React, { memo, MouseEvent } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Flex } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import clsx from 'clsx';
import { useAppSelector } from 'app/hooks';
import useIsFeatureAvailable from 'hooks/useIsFeatureAvailable';
import LazySvg from 'components/LazySvg';
import If from 'components/If';
import { LINKS } from 'utils/constants';
import { SERVICES } from 'utils/service-entitlements-config';
import { getFromBrandConfig } from 'brand-config';
import { QaDashboardPage } from 'testdata/constants';
import { selectUnreadAlertsCount } from 'services/alerts/alertsSelectors';
import { MenuItem, NavLinkItemProps } from './types';
import styles from './index.module.scss';

function getItem(
  key: React.Key,
  label: React.ReactNode,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: 'group',
  qaSelector?: string,
): MenuItem {
  return {
    key,
    label,
    icon,
    children,
    type,
    'data-qa-selector': qaSelector,
  } as MenuItem;
}

function NavLinkItem(props: NavLinkItemProps) {
  const { navigateTo = '/', label, icon, className = '' } = props;
  const navigate = useNavigate();
  const qaSelector = `sidebar_${label?.toString().toLowerCase().replaceAll(' ', '-')}`;

  // Navlink Item click handler
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (navigateTo === '/dark-web-scan-report') {
      window.open(LINKS.cyexScan, '_blank', 'noopener,noreferrer');
    } else {
      navigate(navigateTo);
    }
  };

  return (
    <NavLink
      to={navigateTo}
      data-qa-selector={qaSelector}
      className={({ isActive }) =>
        clsx(styles.link, {
          [styles.active]: isActive,
          [styles.strokeLink]: !!className,
        })
      }
      onClick={handleClick}
    >
      <LazySvg name={icon} className={styles.menuIcon} />
      {label}
    </NavLink>
  );
}

const MomoizedNavLinkItem = memo(NavLinkItem);

function useSidebarMenuItems() {
  const unreadAlertsCount = useAppSelector(selectUnreadAlertsCount);

  /* Getting user access to the services based on entitlments */
  const hasAlerts = useIsFeatureAvailable(SERVICES.alerts);
  const hasVpnInTouch = useIsFeatureAvailable(SERVICES.vpnInTouch);
  const hasPasswordDefense = useIsFeatureAvailable(SERVICES.passwordDefense);
  const hasDboo = useIsFeatureAvailable(SERVICES.dboo);
  const hasPasswords = useIsFeatureAvailable(SERVICES.passwords);
  const hasDigitalVault = useIsFeatureAvailable(SERVICES.digitalVault);
  const hasMedicalDataMonitoring = useIsFeatureAvailable(
    SERVICES.medicalDataMonitoring,
  );
  const hasDarkweb = useIsFeatureAvailable(SERVICES.darkweb);
  const hasDarkwebScanReport = useIsFeatureAvailable(
    SERVICES.darkwebScanReport,
  );
  const hasPasswordScan = useIsFeatureAvailable(SERVICES.passwordScan);
  const hasInsurance = useIsFeatureAvailable(SERVICES.insurance);
  const hasHtmon = useIsFeatureAvailable(SERVICES.htmon);
  const hasDownloadsRecommended = getFromBrandConfig('downloadsRecommended');
  const hasCreditScore = useIsFeatureAvailable(SERVICES.creditScore);

  const menuItems: ItemType[] = [
    getItem(
      'dashboard',
      <MomoizedNavLinkItem
        navigateTo='/dashboard'
        label='Dashboard'
        icon='dashboard'
      />,
    ),
  ];
  const downloadMenuItems: ItemType[] = [];
  const moreFeatureMenuItems: ItemType[] = [];

  if (hasAlerts) {
    menuItems.push(
      getItem(
        'alerts',
        <MomoizedNavLinkItem
          navigateTo='/alerts'
          label='Alerts'
          icon={unreadAlertsCount ? 'alerts' : 'alertsZero'}
        />,
      ),
    );
  }

  /* Download menu items */
  if (hasVpnInTouch) {
    downloadMenuItems.push(
      getItem(
        'vpn-in-touch',
        <MomoizedNavLinkItem
          navigateTo='/vpn-in-touch'
          label='VPN in Touch'
          icon='shield-check'
        />,
      ),
    );
  }
  if (hasPasswordDefense) {
    downloadMenuItems.push(
      getItem(
        'password-defense',
        <MomoizedNavLinkItem
          navigateTo='/password-defense'
          label='Password Defense'
          icon='passwordManager'
        />,
      ),
    );
  }

  /* More features menu items */
  if (hasDboo) {
    moreFeatureMenuItems.push(
      getItem(
        'data-broker-opt-out',
        <MomoizedNavLinkItem
          navigateTo='/data-broker-opt-out'
          label='Data Broker Opt Out'
          icon='dataBrokerOptout'
        />,
      ),
    );
  }
  if (hasPasswords) {
    moreFeatureMenuItems.push(
      getItem(
        'passwords',
        <MomoizedNavLinkItem
          navigateTo='/passwords'
          label='Passwords'
          icon='key'
        />,
      ),
    );
  }
  if (hasDigitalVault) {
    moreFeatureMenuItems.push(
      getItem(
        'digital-vault',
        <MomoizedNavLinkItem
          navigateTo='/digital-vault'
          label='Digital Vault'
          icon='digitalVault'
        />,
      ),
    );
  }
  if (hasMedicalDataMonitoring) {
    moreFeatureMenuItems.push(
      getItem(
        'medical-data-monitoring',
        <NavLinkItem
          navigateTo='/medical-data-monitoring'
          label='Medical Data Monitoring'
          icon='medical-square'
          className='strokeLink'
        />,
      ),
    );
  }
  if (hasDarkweb) {
    moreFeatureMenuItems.push(
      getItem(
        'dark-web',
        <MomoizedNavLinkItem
          navigateTo='/dark-web'
          label='Dark Web Watchlist'
          icon='global-search'
        />,
      ),
    );
  }
  if (hasDarkwebScanReport) {
    moreFeatureMenuItems.push(
      getItem(
        'dark-web-scan-report',
        <MomoizedNavLinkItem
          navigateTo='/dark-web-scan-report'
          label={
            <>
              Dark Web Scan Report <LazySvg name='arrow-up-right' />
            </>
          }
          icon='darkweb-scan-report'
        />,
      ),
    );
  }
  if (hasPasswordScan) {
    moreFeatureMenuItems.push(
      getItem(
        'password-scan',
        <MomoizedNavLinkItem
          navigateTo='/password-scan'
          label='Password Scan'
          icon='passwordScan'
        />,
      ),
    );
  }
  if (hasInsurance) {
    moreFeatureMenuItems.push(
      getItem(
        'insurance',
        <MomoizedNavLinkItem
          navigateTo='/insurance'
          label='Insurance'
          icon='insurance-shape'
        />,
      ),
    );
  }
  if (hasHtmon) {
    moreFeatureMenuItems.push(
      getItem(
        'home-title-monitoring',
        <MomoizedNavLinkItem
          navigateTo='/home-title-monitoring'
          label='Home Title Monitoring'
          icon='home-title'
        />,
      ),
    );
  }
  if (hasCreditScore) {
    moreFeatureMenuItems.push(
      getItem(
        'credit-score',
        <MomoizedNavLinkItem
          navigateTo='/credit-score'
          label='Credit Score'
          icon='credit-score'
        />,
      ),
    );
  }
  if (hasCreditScore) {
    moreFeatureMenuItems.push(
      getItem(
        'credit-tracker',
        <MomoizedNavLinkItem
          navigateTo='/credit-tracker'
          label='Credit Tracker'
          icon='credit-tracker'
        />,
      ),
    );
  }

  /* Pushing download menu items into the sidebar menu items if user has access to atlease one of them */
  if (downloadMenuItems.length) {
    menuItems.push(
      getItem(
        'downloads',
        <Flex align='center' justify='space-between' className={styles.link}>
          <strong className={styles.downloads}>Downloads</strong>
          <If condition={hasDownloadsRecommended}>
            <div className={styles.recommendedBadge}>Recommended</div>
          </If>
        </Flex>,
        null,
        downloadMenuItems,
        'group',
      ),
    );
  }

  /* Pushing more feature menu items into the sidebar menu items if user has access to atlease one of them */
  if (moreFeatureMenuItems.length) {
    menuItems.push(
      getItem(
        'moreFeatures',
        'More features',
        null,
        moreFeatureMenuItems,
        undefined,
        QaDashboardPage.dropdownMoreFeatures,
      ),
    );
  }

  return menuItems;
}

export default useSidebarMenuItems;
